import React, { ReactNode } from 'react';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import AnchorIcon from '@/assets/icons/anchor';
import DiscountIcon from '@/assets/icons/discount';
import EdgeComboIcon from '@/assets/icons/edge-combo-icon.js';
import { FreePickIcon } from '@/assets/icons/free-pick-icon';
import { InflatedIcon } from '@/assets/icons/inflated';
import { NukeIcon } from '@/assets/icons/nuke-icon';
import RocketIcon from '@/assets/icons/rocket';
import { getThemeColorHexValue } from '@/components/lib/theme';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { checkIfPromoProjection, checkIfRegularProjection } from '@/feature/lobby/utils/lineupsUtils';
import { ProjectionType } from '@/types/api.generated';

import { getColorByProjType } from './getProjectionsTypes';

export const getIconByProjType = ({
    type,
    specialIncrease,
    color,
    width,
    height,
}: {
    type: ProjectionType;
    specialIncrease?: boolean;
    color?: string;
    width?: number;
    height?: number;
}): ReactNode | null => {
    const iconColor = color ?? getThemeColorHexValue(getColorByProjType(type, specialIncrease));

    const discountedIcon = specialIncrease ? (
        <InflatedIcon testID="inflated-icon" color={iconColor} width={width ?? 16} height={height ?? 16} />
    ) : (
        <DiscountIcon testID="discount-icon" color={iconColor} width={width ?? 16} height={height ?? 16} />
    );

    switch (type) {
        case ProjectionType.Boosted:
        case ProjectionType.MiniBoosted:
        case ProjectionType.SuperBoosted:
        case ProjectionType.Boosted4:
            return <RocketIcon testID="rocket-icon" color={iconColor} width={width} height={height} />;
        case ProjectionType.Special: {
            return discountedIcon;
        }
        case ProjectionType.FreePick:
            return <FreePickIcon testID="free-pick-icon" color={iconColor} width={width} height={height} />;
        case ProjectionType.Edge1:
        case ProjectionType.Edge2:
        case ProjectionType.Edge3:
        case ProjectionType.Edge4:
            return <EdgeComboIcon testID="edge-combo-icon" color={iconColor} width={width} height={height} />;
        case ProjectionType.Anchor:
            return <AnchorIcon testID="anchor-icon" color={iconColor} width={width} height={height} />;
        case ProjectionType.Nuke:
            return <NukeIcon testID="nuke-icon" color={iconColor} width={width} height={height} />;
        case ProjectionType.Regular:
        default:
            return null;
    }
};

export const isProjectionAndOutcomeSelected = (
    outcome: string,
    pick: PlayerProjectionFragment,
    playerPicked?: BetslipPick
): boolean => {
    return !!playerPicked && playerPicked.outcome === outcome && isProjectionSelected(pick, playerPicked);
};

export const isProjectionSelected = (projection: PlayerProjectionFragment, playerPicked?: BetslipPick) => {
    if (!playerPicked) {
        return false;
    }
    return projection.marketId === playerPicked.projection.marketId;
};

// regular projections show the `value`, the others the `nonRegularValue`
export const getDisplayedProjectionValue = (projection?: PlayerProjectionFragment): number | undefined =>
    checkIfRegularProjection(projection?.type) ? projection?.value : projection?.nonRegularValue;

/**
 * Matches existing markets in the player-props-store or in the betslip-store, such that we avoid duplicates.
 * Same market IDs will match.
 * Same market key and type will match, so that we dont end up with duplicates. [eg: not allowing value 20 edge, and value 21 edge]
 * Same values will match, so that we dont end up with duplicates. [eg: not allowing value 20 edge, and value 20 regular]
 */
export const areProjectionsMatching = (a: PlayerProjectionFragment, b: PlayerProjectionFragment): boolean => {
    if (a.marketId === b.marketId) {
        return true;
    }
    if (a.key === b.key && a.type === b.type) {
        return true;
    }
    if (a.key === b.key) {
        const haveSameValue = getDisplayedProjectionValue(a) === getDisplayedProjectionValue(b);
        //If one of the projection is promo (nuke, anchor, discounted), we don't consider them matching even if they have the same value
        //as we want to allow both to be shown -
        const nonPromoProjections = !checkIfPromoProjection(a.type) && !checkIfPromoProjection(b.type);
        return haveSameValue && nonPromoProjections;
    }
    return false;
};

/**
 * Checks if two projections are the same by comparing their key and type.
 * This is used to determine if a specific projection has been picked.
 *
 * @param projectionA - The first projection to compare (can be nullable)
 * @param projectionB - The second projection to compare (can be nullable)
 * @returns true if both projections have the same key and type, false otherwise
 */
export const isSameProjection = (
    projectionA?: Pick<PlayerProjectionFragment, 'key' | 'type'> | null,
    projectionB?: Pick<PlayerProjectionFragment, 'key' | 'type'> | null
): boolean => {
    if (!projectionA || !projectionB) {
        return false;
    }
    return projectionA.key === projectionB.key && projectionA.type === projectionB.type;
};
